import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  StyleProp,
  ViewStyle,
} from 'react-native';

/**
 * EmptyState — the standard empty/error/loading-fallback layout for the
 * Padrinho App.
 *
 * Visual language is taken from the Sobriety Clock empty state (see
 * `components/SobrietyClockFull.tsx`), which is the canonical example:
 *   - 40x40 #F0EEE4 rounded square holding a 16px monochrome icon
 *   - optional eyebrow (small Inter_500Medium 14, e.g. today's date)
 *   - large Sentient-Regular 24px title centred
 *   - optional Inter_500Medium 14 subtitle
 *   - optional black pill action (#151515, 40h, 12 radius)
 *
 * Use this component for ANY empty / error / "nothing here yet" surface
 * in the app instead of rolling a one-off layout. New surfaces should
 * reach for this first; if the design genuinely needs something
 * different, talk to design before forking the pattern.
 *
 * See `documentation/01. Platform/00. DESIGN SYSTEM/02. COMPONENT_MAP.md`
 * (Mobile — Padrinho App > EmptyState) for the full prop table, token
 * usage, copy conventions, and migration checklist.
 */

interface EmptyStateAction {
  label: string;
  onPress: () => void;
  /** Show a spinner inside the button instead of the label. */
  loading?: boolean;
  /** Disable the button (greyed-out, non-interactive). */
  disabled?: boolean;
}

interface EmptyStateProps {
  /**
   * Optional 16px monochrome icon. Pass a rendered icon component
   * (e.g. `<FlagIcon size={16} color="#151515" />`). It will be placed
   * inside a 40x40 #F0EEE4 rounded square. Omit for icon-less states.
   */
  icon?: React.ReactNode;
  /**
   * Optional small text shown above the title — usually a date,
   * timestamp, or category eyebrow. Inter_500Medium 14, #151515.
   */
  eyebrow?: string;
  /**
   * Required main message. Sentient-Regular 24, #151515, centred.
   * Keep it short — one line ideally, two max.
   */
  title: string;
  /**
   * Optional supporting text below the title for context.
   * Inter_400Regular 14, #7E7D75, centred.
   */
  subtitle?: string;
  /**
   * Optional primary action button. Black pill matching the sobriety
   * clock "Começar" CTA. Omit for purely informational empty states.
   */
  action?: EmptyStateAction;
  /**
   * Layout variant.
   *
   * - `fill` (default): expands to fill the parent (uses `flex: 1`)
   *   and centres vertically. Use when the empty state owns the whole
   *   screen / tab body, like the Sobriety Clock.
   *
   * - `top`: top-aligned with `paddingTop: 60`. Use inside a
   *   ScrollView or when the empty state shares space with other
   *   content above/below it (e.g. the Missões error inside the
   *   diary scroll view).
   */
  variant?: 'fill' | 'top';
  /** Optional style overrides for the outer wrapper. */
  style?: StyleProp<ViewStyle>;
  /** Optional testID for E2E testing. */
  testID?: string;
}

export default function EmptyState({
  icon,
  eyebrow,
  title,
  subtitle,
  action,
  variant = 'fill',
  style,
  testID,
}: EmptyStateProps) {
  const isTop = variant === 'top';

  return (
    <View
      style={[isTop ? styles.wrapperTop : styles.wrapperFill, style]}
      testID={testID}
    >
      <View style={isTop ? styles.groupTop : styles.groupFill}>
        {icon ? (
          <View style={styles.iconBox}>
            <View style={styles.iconSquare}>{icon}</View>
          </View>
        ) : null}

        <View style={styles.textGroup}>
          {eyebrow ? <Text style={styles.eyebrow}>{eyebrow}</Text> : null}
          <Text style={styles.title}>{title}</Text>
          {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
        </View>

        {action ? (
          <TouchableOpacity
            style={[styles.button, action.disabled && styles.buttonDisabled]}
            onPress={
              action.disabled || action.loading ? undefined : action.onPress
            }
            disabled={action.disabled || action.loading}
            activeOpacity={0.8}
            accessibilityRole="button"
            accessibilityLabel={action.label}
            accessibilityState={{
              disabled: !!action.disabled,
              busy: !!action.loading,
            }}
          >
            {action.loading ? (
              <ActivityIndicator color="#FFFFFF" size="small" />
            ) : (
              <Text style={styles.buttonText}>{action.label}</Text>
            )}
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  // 'fill' fills its parent and centres vertically — owns the screen.
  wrapperFill: {
    flex: 1,
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  groupFill: {
    flex: 1,
    padding: 16,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 24,
  },
  // 'top' sits at the top with breathing room — for inline error
  // states inside scroll views.
  wrapperTop: {
    paddingHorizontal: 32,
    paddingTop: 60,
    paddingBottom: 24,
  },
  groupTop: {
    alignItems: 'center',
    gap: 24,
  },
  iconBox: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  iconSquare: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textGroup: {
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  eyebrow: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 19.6,
  },
  title: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 28.8,
  },
  subtitle: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#7E7D75',
    textAlign: 'center',
    lineHeight: 19.6,
    marginTop: 4,
  },
  button: {
    backgroundColor: '#151515',
    borderRadius: 12,
    paddingHorizontal: 20,
    height: 40,
    minWidth: 120,
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonDisabled: {
    opacity: 0.4,
  },
  buttonText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#FFFFFF',
    lineHeight: 19.6,
  },
});
